import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchLatestProductsByOrganization,
  fetchProductsByOrganization,
  fetchProductsByOrganizationAndBranch,
  fetchProductsWorthByOrganizationAndBranch,
} from "../../api/products";
import { storage } from "../../helpers/storage";

const initialState = {
  value: {
    lastParams: {
      organization_id: null,
      branch_id: null,
      keyword: "",
      category: "",
      sort: "desc",
      sortBy: "created_at",
    },
    lastPage: 1,
    currentPage: 1,
    listStyle: "list",
    limit: process.env.REACT_APP_PAGE_LIMIT || 30,
    loading: false,
    loadingNextPage: false,
    loadingBackground: false,
    loadBackgroundFailed: false,
    allProductsLoading: false,
    allProductsLoaded: false,
    lastUpdate: null,
    products: [],
    worthParams: {
      organization_id: null,
      branch_id: null,
    },
    worthData: {
      total: 0,
      worth: 0,
    },
    error: null,
  },
};

const getFilterProducts = (
  storageProducts,
  currentParams,
  page,
  limit,
  dispatch
) => {
  const filteredProducts = storageProducts
    ?.filter((product) => {
      const hasBranch = product.branch_stocks?.some(
        (stock) => stock.branch_id === currentParams.branch_id
      );
      if (!hasBranch) return false;

      const categoryMatch =
        currentParams.category === "highlighted"
          ? product.highlight
          : currentParams.category === "" ||
            product.categories?.some(
              (cat) => cat.id === currentParams.category
            );
      if (!categoryMatch) return false;

      const keywordMatch =
        !currentParams.keyword ||
        new RegExp(currentParams.keyword, "i").test(product.name);

      return keywordMatch;
    })
    .sort((a, b) => {
      const { sortBy, sort } = currentParams;

      let aVal, bVal;

      if (sortBy === "price") {
        const aPrice =
          Number(a.reduced_price) > 0
            ? Number(a.reduced_price)
            : Number(a.price);
        const bPrice =
          Number(b.reduced_price) > 0
            ? Number(b.reduced_price)
            : Number(b.price);
        aVal = aPrice;
        bVal = bPrice;
      } else if (sortBy === "current_stock") {
        const aStock =
          a.branch_stocks?.find((s) => s.branch_id === currentParams.branch_id)
            ?.current_stock ?? 0;
        const bStock =
          b.branch_stocks?.find((s) => s.branch_id === currentParams.branch_id)
            ?.current_stock ?? 0;
        aVal = aStock;
        bVal = bStock;
      } else {
        aVal = a[sortBy];
        bVal = b[sortBy];
      }

      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sort === "asc" ? -1 : 1;
      if (aVal > bVal) return sort === "asc" ? 1 : -1;
      return 0;
    });

  const totalProducts = page * limit;
  if (dispatch) {
    const totalPages = Math.ceil((filteredProducts?.length || 0) / limit) || 1;
    dispatch(updateProductsLastPage({ lastPage: totalPages }));
  }
  const products = filteredProducts
    ? filteredProducts?.slice(0, totalProducts)
    : [];

  return products;
};

export const loadAllProducts = createAsyncThunk(
  "products/loadAllProducts",
  async ({ organization_id }, { rejectWithValue }) => {
    try {
      const response = await fetchProductsByOrganization(organization_id);
      return {
        allProducts: response?.data?.products || [],
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch products worth."
      );
    }
  }
);

export const loadProductsWorth = createAsyncThunk(
  "products/loadProductsWorth",
  async ({ organization_id, branch_id }, { getState, rejectWithValue }) => {
    try {
      const response = await fetchProductsWorthByOrganizationAndBranch(
        organization_id,
        branch_id
      );
      return {
        count: response.data?.count || 0,
        worth: response.data?.worth || 0,
        currentParams: {
          organization_id: organization_id,
          branch_id: branch_id,
        },
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch products worth."
      );
    }
  }
);

export const loadProductsWorthIfNeeded = createAsyncThunk(
  "products/loadManageProductsWorthIfNeeded",
  async ({ organization_id, branch_id }, { getState, dispatch }) => {
    const state = getState().productReducer;

    const currentParams = {
      organization_id,
      branch_id,
    };

    const previousParams = {
      organization_id: state.value.worthParams?.organization_id,
      branch_id: state.value.worthParams?.branch_id,
    };

    let storageProductWorth = await storage.get("productWorthData");
    storageProductWorth = storageProductWorth
      ? JSON.parse(storageProductWorth)
      : null;

    if (storageProductWorth) {
      dispatch(updateProductWorth(storageProductWorth));
    }

    if (JSON.stringify(currentParams) === JSON.stringify(previousParams)) {
      return;
    }

    return dispatch(loadProductsWorth({ organization_id, branch_id }));
  }
);

export const loadProducts = createAsyncThunk(
  "products/loadProducts",
  async (
    { organization_id, branch_id, keyword, category, sort, sortBy },
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      const state = getState().productReducer;

      const effectiveKeyword = keyword || "";
      const effectiveCategory = category || "";
      const effectiveBranchId = branch_id || state.value.lastParams.branch_id;
      const effectiveLimit = state.value.limit;

      const isLoadingAllProducts = state.value.allProductsLoading;
      const isLoadedAllProducts = state.value.allProductsLoaded;

      if (isLoadedAllProducts) {
        let storageProducts = await storage.get("products");
        storageProducts = storageProducts ? JSON.parse(storageProducts) : [];

        const filteredProducts = getFilterProducts(
          storageProducts,
          {
            organization_id: organization_id,
            branch_id: effectiveBranchId,
            keyword: effectiveKeyword,
            category: effectiveCategory,
            sort: sort,
            sortBy: sortBy,
          },
          1,
          effectiveLimit,
          dispatch
        );

        return {
          products: filteredProducts,
          lastPage: state.value.lastPage,
          newParams: {
            organization_id: organization_id,
            branch_id: effectiveBranchId,
            keyword: effectiveKeyword,
            category: effectiveCategory,
            sort: sort,
            sortBy: sortBy,
          },
          ignore: true,
        };
      } else {
        const response = await fetchProductsByOrganizationAndBranch(
          organization_id,
          effectiveBranchId,
          effectiveKeyword,
          effectiveCategory,
          1,
          effectiveLimit,
          sort,
          sortBy
        );

        if (!isLoadingAllProducts) {
          dispatch(loadAllProducts({ organization_id }));
        }

        return {
          products: response.data?.products || [],
          lastPage: response.data?.lastPage || 1,
          newParams: {
            organization_id: organization_id,
            branch_id: effectiveBranchId,
            keyword: effectiveKeyword,
            category: effectiveCategory,
            sort: sort,
            sortBy: sortBy,
          },
          ignore: false,
        };
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch products."
      );
    }
  }
);

export const reloadProductsInBackground = createAsyncThunk(
  "products/reloadProductsInBackground",
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState().productReducer;

      const {
        lastParams: { organization_id, branch_id },
        lastUpdate,
        allProductsLoaded,
        worthData: { total, worth },
      } = state.value;

      if (lastUpdate) {
        let storageProducts = await storage.get("products");
        storageProducts = storageProducts
          ? JSON.parse(storageProducts)
          : state.value.products;

        const response = await fetchLatestProductsByOrganization(
          organization_id,
          lastUpdate
        );

        if (response.data?.products?.length > 0) {
          dispatch(
            updateExistingProductInternal({
              products: response.data?.products,
              previousProducts: storageProducts,
            })
          );
        }

        if (response.data?.deleted_products?.length > 0) {
          response.data?.deleted_products?.forEach((el) => {
            dispatch(
              deleteExistingProductInternal({
                product_id: el,
                previousProducts: storageProducts,
              })
            );
          });
        }

        if (response.data?.lastPage) {
          dispatch(
            updateProductsLastPage({ lastPage: response.data?.lastPage })
          );
        }

        if (response.data?.lastUpdate) {
          storage.set(
            "productsState",
            JSON.stringify({
              loaded: allProductsLoaded,
              time: response.data?.lastUpdate,
            })
          );
          dispatch(updateLastUpdateDate({ date: response.data?.lastUpdate }));
        }
      }

      const worthResponse = await fetchProductsWorthByOrganizationAndBranch(
        organization_id,
        branch_id
      );

      return {
        count: worthResponse.data?.count || total,
        worth: worthResponse.data?.worth || worth,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch background products."
      );
    }
  }
);

export const loadProductsIfNeeded = createAsyncThunk(
  "products/loadManageProductsIfNeeded",
  async (
    { organization_id, branch_id, keyword, category, sort, sortBy },
    { getState, dispatch }
  ) => {
    const state = getState().productReducer;

    const currentParams = {
      organization_id,
      branch_id,
      keyword,
      category,
      sort,
      sortBy,
    };

    const previousParams = {
      organization_id: state.value.lastParams?.organization_id,
      branch_id: state.value.lastParams?.branch_id,
      keyword: state.value.lastParams?.keyword,
      category: state.value.lastParams?.category,
      sort: state.value.lastParams?.sort,
      sortBy: state.value.lastParams?.sortBy,
    };

    let storageProducts = await storage.get("products");
    storageProducts = storageProducts ? JSON.parse(storageProducts) : null;

    let storageProductState = await storage.get("productsState");
    storageProductState = storageProductState
      ? JSON.parse(storageProductState)
      : null;

    if (storageProducts && storageProducts?.length) {
      if (storageProductState) {
        dispatch(
          updateProductsState({
            allProductsLoaded: storageProductState?.loaded || false,
            lastUpdate: storageProductState?.time || null,
          })
        );

        dispatch(
          updateProductsLastPage({
            lastPage: storageProductState?.lastPage || 1,
          })
        );
      }

      const filteredProducts = getFilterProducts(
        storageProducts,
        currentParams,
        state.value.currentPage,
        state.value.limit,
        dispatch
      );

      dispatch(updateProducts({ products: filteredProducts }));
      dispatch(updateProductLoading(false));
    } else if (
      JSON.stringify(currentParams) !== JSON.stringify(previousParams)
    ) {
      dispatch(updateProductLoading(true));
    }

    if (
      previousParams.branch_id &&
      previousParams.branch_id !== currentParams.branch_id
    ) {
      dispatch(updateProductLoading(true));
    }

    if (JSON.stringify(currentParams) === JSON.stringify(previousParams)) {
      return dispatch(reloadProductsInBackground(currentParams));
    }

    return dispatch(
      loadProducts({
        organization_id,
        branch_id,
        keyword,
        category,
        sort,
        sortBy,
      })
    );
  }
);

export const loadNextProducts = createAsyncThunk(
  "analytics/loadNextProducts",
  async ({ page }, { getState, rejectWithValue }) => {
    try {
      const state = getState().productReducer;

      const effectiveOrganizationId = state.value.lastParams.organization_id;
      const effectiveKeyword = state.value.lastParams.keyword;
      const effectiveCategory = state.value.lastParams.category;
      const effectiveBranchId = state.value.lastParams.branch_id;
      const effectiveSort = state.value.lastParams.sort;
      const effectiveSortBy = state.value.lastParams.sortBy;
      const effectiveLimit = state.value.limit;

      const isLoadedAllProducts = state.value.allProductsLoaded;

      if (state.value.currentPage === page) {
        return {
          products: [],
          lastPage: state.value.lastPage,
          currentPage: page,
          acceptAll: false,
        };
      } else {
        if (isLoadedAllProducts) {
          let storageProducts = await storage.get("products");
          storageProducts = storageProducts ? JSON.parse(storageProducts) : [];

          const filteredProducts = getFilterProducts(
            storageProducts,
            {
              organization_id: effectiveOrganizationId,
              branch_id: effectiveBranchId,
              keyword: effectiveKeyword,
              category: effectiveCategory,
              sort: effectiveSort,
              sortBy: effectiveSortBy,
            },
            page,
            effectiveLimit
          );

          return {
            products: filteredProducts,
            lastPage: state.value.lastPage,
            currentPage: page,
            acceptAll: true,
          };
        } else {
          const response = await fetchProductsByOrganizationAndBranch(
            effectiveOrganizationId,
            effectiveBranchId,
            effectiveKeyword,
            effectiveCategory,
            page,
            effectiveLimit,
            effectiveSort,
            effectiveSortBy
          );
          return {
            products: response.data?.products || [],
            lastPage: response.data?.lastPage,
            currentPage: page,
            acceptAll: false,
          };
        }
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch products."
      );
    }
  }
);

export const updateExistingProduct = createAsyncThunk(
  "products/updateExistingProduct",
  async ({ products }, { getState, dispatch }) => {
    const state = getState().productReducer;

    let storageProducts = await storage.get("products");
    storageProducts = storageProducts
      ? JSON.parse(storageProducts)
      : state.value.products;

    return dispatch(
      updateExistingProductInternal({
        products: products,
        previousProducts: storageProducts,
      })
    );
  }
);

export const deleteExistingProduct = createAsyncThunk(
  "products/deleteExistingProduct",
  async ({ product_id }, { getState, dispatch }) => {
    const state = getState().productReducer;

    let storageProducts = await storage.get("products");
    storageProducts = storageProducts
      ? JSON.parse(storageProducts)
      : state.value.products;

    return dispatch(
      deleteExistingProductInternal({
        product_id: product_id,
        previousProducts: storageProducts,
      })
    );
  }
);

const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    updateProducts: (state, data) => {
      state.value.products = data.payload.products;
      state.value.currentPage = 1;
    },
    updateProductsState: (state, data) => {
      state.value.allProductsLoaded = data.payload.allProductsLoaded;
      state.value.lastUpdate = data.payload.lastUpdate;
    },
    updateProductsLastPage: (state, data) => {
      state.value.lastPage = data.payload.lastPage;
    },
    updateLastUpdateDate: (state, data) => {
      state.value.lastUpdate = data.payload.date;
    },
    updateExistingProductInternal: (state, data) => {
      const { products, previousProducts } = data.payload;

      products?.forEach((product) => {
        const exists = previousProducts?.findIndex(
          (el) => el?.id === product?.id
        );

        if (exists !== -1) {
          previousProducts[exists] = product;
        } else {
          previousProducts?.push(product);
          state.value.lastPage =
            Math.ceil((previousProducts?.length || 0) / state.value.limit) || 1;
        }
      });

      const filteredProducts = getFilterProducts(
        previousProducts,
        state.value.lastParams,
        state.value.currentPage,
        state.value.limit
      );

      state.value.products = filteredProducts;

      storage.set("products", JSON.stringify(previousProducts));
    },
    deleteExistingProductInternal: (state, data) => {
      const { product_id, previousProducts } = data.payload;

      const exists = previousProducts?.findIndex((el) => el?.id === product_id);

      if (exists !== -1) {
        previousProducts?.splice(exists, 1);

        const filteredProducts = getFilterProducts(
          previousProducts,
          state.value.lastParams,
          state.value.currentPage,
          state.value.limit
        );
        state.value.lastPage =
          Math.ceil((previousProducts?.length || 0) / state.value.limit) || 1;
        state.value.products = filteredProducts;
        storage.set("products", JSON.stringify(previousProducts));
      }
    },
    updateListStyle: (state, data) => {
      state.value.listStyle = data.payload.listStyle;
    },
    updateProductLoading: (state, data) => {
      state.value.loading = data.payload;
      state.value.error = null;
    },
    updateProductWorth: (state, data) => {
      state.value.worthData = data.payload;
    },
    updateLastParams: (state) => {
      state.value.lastParams = {
        organization_id: state.value.lastParams.organization_id,
        branch_id: state.value.lastParams.branch_id,
        keyword: "",
        category: "",
        sort: state.value.lastParams.sort,
        sortBy: state.value.lastParams.sortBy,
      };
    },
    clearProducts: () => {
      return {
        ...initialState,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAllProducts.pending, (state) => {
        state.value.allProductsLoading = true;
      })
      .addCase(loadAllProducts.fulfilled, (state, action) => {
        const { allProducts: products } = action.payload;

        const filteredProducts = getFilterProducts(
          products,
          state.value.lastParams,
          state.value.currentPage,
          state.value.limit
        );
        state.value.lastPage =
          Math.ceil((products?.length || 0) / state.value.limit) || 1;
        state.value.products = filteredProducts;
        state.value.allProductsLoaded = true;
        state.value.allProductsLoading = false;
        const latestUpdate = products.reduce((latest, product) => {
          return new Date(product.updated_at) > new Date(latest.updated_at)
            ? product
            : latest;
        });
        state.value.lastUpdate =
          latestUpdate?.updated_at || new Date().toISOString();

        storage.set("products", JSON.stringify(products));
        storage.set(
          "productsState",
          JSON.stringify({
            loaded: state.value.allProductsLoaded,
            time: state.value.lastUpdate,
            lastPage: state.value.lastPage,
          })
        );
      })
      .addCase(loadAllProducts.rejected, (state) => {
        state.value.allProductsLoading = false;
      });

    builder
      .addCase(loadProductsWorth.pending, (state) => {})
      .addCase(loadProductsWorth.fulfilled, (state, action) => {
        state.value.worthData = {
          total: action.payload.count,
          worth: action.payload.worth,
        };
        state.value.worthParams = action.payload.currentParams;

        storage.set(
          "productWorthData",
          JSON.stringify({
            total: action.payload.count,
            worth: action.payload.worth,
          })
        );
      })
      .addCase(loadProductsWorth.rejected, (state) => {});

    builder
      .addCase(loadProducts.pending, (state) => {})
      .addCase(loadProducts.fulfilled, (state, action) => {
        state.value.loading = false;
        state.value.products = action.payload.products;
        state.value.lastPage = action.payload.lastPage;
        state.value.currentPage = 1;
        state.value.lastParams = action.payload.newParams;

        if (!action.payload.ignore) {
          storage.set("products", JSON.stringify(action.payload.products));
        }
      })
      .addCase(loadProducts.rejected, (state, action) => {
        state.value.loading = false;
        state.value.error = action.payload;
        state.value.lastPage = state.value.currentPage;
      });

    builder
      .addCase(loadNextProducts.pending, (state) => {
        state.value.loadingNextPage = true;
        state.value.error = null;
      })
      .addCase(loadNextProducts.fulfilled, (state, action) => {
        state.value.loadingNextPage = false;
        state.value.lastPage = action.payload.lastPage;
        state.value.currentPage = action.payload.currentPage;

        if (action.payload.acceptAll) {
          state.value.products = action.payload.products;
        } else {
          state.value.products = [
            ...state.value.products,
            ...action.payload.products,
          ];
          storage.set(
            "products",
            JSON.stringify([
              ...state.value.products,
              ...action.payload.products,
            ])
          );
        }
      })
      .addCase(loadNextProducts.rejected, (state, action) => {
        state.value.loadingNextPage = false;
        state.value.error = action.payload;
        state.value.lastPage = state.value.currentPage;
      });

    builder
      .addCase(reloadProductsInBackground.pending, (state) => {
        state.value.loadingBackground = true;
        state.value.loadBackgroundFailed = false;
      })
      .addCase(reloadProductsInBackground.fulfilled, (state, action) => {
        state.value.loadingBackground = false;
        state.value.worthData = {
          total: action.payload.count,
          worth: action.payload.worth,
        };

        storage.set(
          "productWorthData",
          JSON.stringify({
            total: action.payload.count,
            worth: action.payload.worth,
          })
        );
      })
      .addCase(reloadProductsInBackground.rejected, (state, action) => {
        state.value.loadingBackground = false;
        state.value.error = action.payload;
        state.value.loadBackgroundFailed = true;
      });
  },
});

export const {
  updateProducts,
  updateExistingProductInternal,
  deleteExistingProductInternal,
  updateListStyle,
  updateProductLoading,
  updateProductWorth,
  clearProducts,
  updateProductsState,
  updateProductsLastPage,
  updateLastUpdateDate,
  updateLastParams,
} = productSlice.actions;
export default productSlice.reducer;
